// src/common/hooks/useTheme.ts
import { useMemo, useEffect, useState } from "react";
import {
  getClientConfig,
  isRuntimeConfigLoaded,
} from "../../config/runtimeConfig";

export const useTheme = () => {
  const [configReady, setConfigReady] = useState(isRuntimeConfigLoaded());

  useEffect(() => {
    if (!configReady) {
      const checkConfig = setInterval(() => {
        if (isRuntimeConfigLoaded()) {
          setConfigReady(true);
          clearInterval(checkConfig);
        }
      }, 100);
      return () => clearInterval(checkConfig);
    }
  }, [configReady]);

  // Устанавливаем CSS-переменные при загрузке конфига
  useEffect(() => {
    if (configReady) {
      const config = getClientConfig();
      const root = document.documentElement;

      // Определяем тему
      const forceTheme = config.forceTheme || "system";
      const systemDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      const isDark =
        forceTheme === "dark" || (forceTheme === "system" && systemDark);

      // Устанавливаем data-theme атрибут
      root.setAttribute("data-theme", isDark ? "dark" : "light");

      // ========== ЦВЕТА ==========
      // Основные цвета из конфига (для темной темы)
      root.style.setProperty("--primary", config.primaryColor);

      // Фон и текст зависят от темы
      if (isDark) {
        root.style.setProperty("--background", config.backgroundColor);
        root.style.setProperty("--text", config.textColor || "#ffffff");
        root.style.setProperty("--card", "#2c3e50");
        root.style.setProperty("--border", "#34495e");
        root.style.setProperty("--text-secondary", "#9BA1A6");
        root.style.setProperty("--overlay", "rgba(0,0,0,0.7)");
        root.style.setProperty("--on-primary", "#ffffff");
        root.style.setProperty("--on-background", "#ffffff");
        root.style.setProperty("--on-card", "#ffffff");
      } else {
        root.style.setProperty("--background", "#ffffff");
        root.style.setProperty("--text", "#000000");
        root.style.setProperty("--card", "#f5f5f5");
        root.style.setProperty("--border", "#e0e0e0");
        root.style.setProperty("--text-secondary", "#666666");
        root.style.setProperty("--overlay", "rgba(0,0,0,0.5)");
        root.style.setProperty("--on-primary", "#ffffff");
        root.style.setProperty("--on-background", "#000000");
        root.style.setProperty("--on-card", "#000000");
      }

      // Системные цвета
      root.style.setProperty("--success", "#4CAF50");
      root.style.setProperty("--danger", "#f44336");
      root.style.setProperty("--warning", "#ff9800");
      root.style.setProperty("--info", "#2196F3");
      root.style.setProperty("--primary-light", `${config.primaryColor}20`);

      // ========== RGB-версии для rgba() ==========
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
          : "0, 0, 0";
      };

      // Устанавливаем RGB версии основных цветов
      root.style.setProperty(
        "--background-rgb",
        hexToRgb(
          getComputedStyle(root).getPropertyValue("--background").trim(),
        ),
      );
      root.style.setProperty(
        "--card-rgb",
        hexToRgb(getComputedStyle(root).getPropertyValue("--card").trim()),
      );
      root.style.setProperty("--primary-rgb", hexToRgb(config.primaryColor));
      root.style.setProperty(
        "--border-rgb",
        hexToRgb(getComputedStyle(root).getPropertyValue("--border").trim()),
      );

      // ========== РАЗМЕРЫ ШРИФТОВ ==========
      // Базовые размеры (можно переопределить в конфиге клиента)
      root.style.setProperty("--font-size-xs", config.fontSizeXS || "12px");
      root.style.setProperty("--font-size-sm", config.fontSizeSM || "14px");
      root.style.setProperty("--font-size-md", config.fontSizeMD || "16px");
      root.style.setProperty("--font-size-lg", config.fontSizeLG || "18px");
      root.style.setProperty("--font-size-xl", config.fontSizeXL || "20px");
      root.style.setProperty("--font-size-2xl", config.fontSize2XL || "24px");
      root.style.setProperty("--font-size-3xl", config.fontSize3XL || "28px");
      root.style.setProperty("--font-size-4xl", config.fontSize4XL || "32px");

      // Веса шрифтов
      root.style.setProperty(
        "--font-weight-regular",
        config.fontWeightRegular || "400",
      );
      root.style.setProperty(
        "--font-weight-medium",
        config.fontWeightMedium || "500",
      );
      root.style.setProperty(
        "--font-weight-semiBold",
        config.fontWeightSemiBold || "600",
      );
      root.style.setProperty(
        "--font-weight-bold",
        config.fontWeightBold || "700",
      );

      // Семейство шрифтов
      root.style.setProperty(
        "--font-family",
        config.fontFamily || "'Jost', sans-serif",
      );

      // ========== ПРОМО-ШРИФТ ==========
      root.style.setProperty(
        "--font-family-promo",
        config.promoFont || config.fontFamily || "'Jost', sans-serif",
      );

      // ========== ЗАГРУЗКА ДОПОЛНИТЕЛЬНЫХ ШРИФТОВ ==========
      if (config.googleFonts && config.googleFonts.length > 0) {
        config.googleFonts.forEach((url) => {
          const existing = document.querySelector(`link[href="${url}"]`);
          if (!existing) {
            const link = document.createElement("link");
            link.href = url;
            link.rel = "stylesheet";
            document.head.appendChild(link);
          }
        });
      }
    }
  }, [configReady]);

  // Возвращаем isDark для тех мест, где нужно
  const isDark = useMemo(() => {
    return document.documentElement.getAttribute("data-theme") === "dark";
  }, [configReady]);

  // Получаем текущие значения CSS переменных
  const theme = useMemo(() => {
    const styles = getComputedStyle(document.documentElement);
    return {
      // Цвета
      primary: styles.getPropertyValue("--primary").trim(),
      background: styles.getPropertyValue("--background").trim(),
      text: styles.getPropertyValue("--text").trim(),
      card: styles.getPropertyValue("--card").trim(),
      border: styles.getPropertyValue("--border").trim(),
      textSecondary: styles.getPropertyValue("--text-secondary").trim(),
      overlay: styles.getPropertyValue("--overlay").trim(),
      success: styles.getPropertyValue("--success").trim(),
      danger: styles.getPropertyValue("--danger").trim(),
      warning: styles.getPropertyValue("--warning").trim(),
      info: styles.getPropertyValue("--info").trim(),
      onCard: styles.getPropertyValue("--on-card").trim(),
      onBackground: styles.getPropertyValue("--on-background").trim(),
      onPrimary: styles.getPropertyValue("--on-primary").trim(),

      // Шрифты
      fontSizeXS: styles.getPropertyValue("--font-size-xs").trim(),
      fontSizeSM: styles.getPropertyValue("--font-size-sm").trim(),
      fontSizeMD: styles.getPropertyValue("--font-size-md").trim(),
      fontSizeLG: styles.getPropertyValue("--font-size-lg").trim(),
      fontSizeXL: styles.getPropertyValue("--font-size-xl").trim(),
      fontSize2XL: styles.getPropertyValue("--font-size-2xl").trim(),
      fontSize3XL: styles.getPropertyValue("--font-size-3xl").trim(),
      fontFamily: styles.getPropertyValue("--font-family").trim(),
      promoFont: styles.getPropertyValue("--font-family-promo").trim(),
    };
  }, [configReady]);

  return { isDark, theme };
};
